import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/api/client";
import type { CreateTripRequest, VersionedRequest } from "@/api/types";
import { queryKeys } from "./queryKeys";
import { useConflictToast } from "./useConflictToast";

export function useTrip(tripId: string | undefined) {
  return useQuery({
    queryKey: tripId ? queryKeys.trip(tripId) : ["trip", "none"],
    queryFn: () => api.getTrip(tripId!),
    enabled: !!tripId,
    retry: false,
  });
}

export function useCreateTrip() {
  return useMutation({
    mutationFn: (body: CreateTripRequest) => api.createTrip(body),
  });
}

export function useCloseTrip(tripId: string) {
  const qc = useQueryClient();
  const onConflict = useConflictToast();
  return useMutation({
    mutationFn: (body: VersionedRequest) => api.closeTrip(tripId, body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.trip(tripId) });
      void qc.invalidateQueries({ queryKey: queryKeys.balances(tripId) });
      void qc.invalidateQueries({ queryKey: queryKeys.settlements(tripId) });
    },
    onError: (err) => {
      onConflict(err, [queryKeys.trip(tripId)]);
    },
  });
}

export function useReopenTrip(tripId: string) {
  const qc = useQueryClient();
  const onConflict = useConflictToast();
  return useMutation({
    mutationFn: (body: VersionedRequest) => api.reopenTrip(tripId, body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.trip(tripId) });
      void qc.invalidateQueries({ queryKey: queryKeys.balances(tripId) });
      void qc.invalidateQueries({ queryKey: queryKeys.settlements(tripId) });
    },
    onError: (err) => onConflict(err, [queryKeys.trip(tripId)]),
  });
}
